/**
 * The page a seller lands on.
 *
 * Two links have been going out for months that led nowhere: the Vehicle Purchase Agreement
 * SMS and the DD Collection email both pointed at paths that were never
 * routes, so the seller got the DirectAuto login screen — a login for a system
 * they have no account for. This is the page those links should have opened.
 *
 * Everything here is written for someone who has never seen the platform,
 * is probably standing next to the car, and is almost certainly on a phone:
 *
 *   - no jargon. "Vehicle Purchase Agreement", "your details", "what happens next".
 *   - one thing to do at a time. Sign first, documents second, because that is
 *     the order the workflow needs and asking for ID before there is a signed
 *     contract is the wrong way round.
 *   - it never claims something worked without checking. If the signature did
 *     not store, the seller is told it did not store — a seller who walks away
 *     believing they have signed is the worst outcome this page can produce.
 *
 * The rest of the app renders a "DirectAuto is desktop-only" notice. This page
 * deliberately does not: it is mounted from the public route tree in App.tsx,
 * ahead of that notice, because a seller with only a phone still has to be
 * able to sign.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import './SellerPortal.css'
import FileDropZone from './FileDropZone'

interface RequiredDoc {
  key: string
  label: string
  optional: boolean
  received: boolean
}

interface PortalData {
  seller: {
    name: string
    legalName: string
    type: string
    address: string
    mobile: string
    email: string
    suburb: string
    state: string
    postcode: string
    abn: string
    lmct: string
  }
  vehicle: { description: string; vin: string; rego: string; odometer: number | null }
  agreedPrice: number
  billOfSale: {
    status: 'signed' | 'awaiting_signature' | 'waiting_for_documents' | 'not_required' | 'closed'
    signedAt: string | null
    signedByName: string | null
    expiresInDays: number
    declarations?: string[]
    clauses?: { title: string; body: string }[]
  }
  documents: {
    status: 'not_requested' | 'open' | 'closed' | 'waiting_for_signature'
    outstanding: number
    required: RequiredDoc[]
    expiresInDays: number
    received: { type: string; filename: string | null; uploadedAt: string }[]
  }
}

const money = (n: number) =>
  `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`

const when = (iso: string | null) => {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-AU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

/* ------------------------------------------------------------------ pad --- */

/**
 * A signature pad that works with a finger.
 *
 * Written here rather than reusing ESignatureCanvas because that component
 * talks to Supabase directly, and a seller has no session — it would fail at
 * row-level security. This one draws, and hands the strokes upwards.
 */
function SignaturePad({ onChange }: { onChange: (svg: string | null) => void }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const drawing = useRef(false)
  const strokes = useRef<{ x: number; y: number }[][]>([])
  const current = useRef<{ x: number; y: number }[]>([])
  const sized = useRef({ w: 0, h: 0 })
  const [hasInk, setHasInk] = useState(false)

  /* Setting canvas.width wipes the bitmap, so anything that re-sizes has to put the
     strokes back afterwards or the seller watches their signature disappear. */
  const repaint = useCallback(() => {
    const canvas = canvasRef.current
    const ctx = canvas && canvas.getContext('2d')
    if (!canvas || !ctx) return
    const all = strokes.current.concat(current.current.length ? [current.current] : [])
    for (const s of all) {
      if (s.length < 2) continue
      ctx.beginPath()
      ctx.moveTo(s[0].x, s[0].y)
      for (let i = 1; i < s.length; i += 1) ctx.lineTo(s[i].x, s[i].y)
      ctx.stroke()
    }
  }, [])

  /* iOS Safari fires resize every time the address bar slides - including while a finger
     is on the pad. The old version re-sized on every one of those, which cleared the
     canvas mid-stroke. Only touch the bitmap when the size has actually changed. */
  const size = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    const w = Math.round(rect.width * dpr)
    const h = Math.round(rect.height * dpr)
    if (!w || !h) return
    if (w === sized.current.w && h === sized.current.h) return
    sized.current = { w, h }
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.scale(dpr, dpr)
    ctx.lineWidth = 2.2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#111'
    repaint()
  }, [repaint])

  useEffect(() => {
    size()
    window.addEventListener('resize', size)
    return () => window.removeEventListener('resize', size)
  }, [size])

  const emit = () => {
    const canvas = canvasRef.current
    const w = canvas ? canvas.getBoundingClientRect().width : 300
    const h = canvas ? canvas.getBoundingClientRect().height : 150
    const paths = strokes.current
      .filter((s) => s.length > 1)
      .map(
        (s) =>
          '<path d="M ' +
          s.map((p) => p.x.toFixed(1) + ' ' + p.y.toFixed(1)).join(' L ') +
          '" fill="none" stroke="#111" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>'
      )
      .join('')
    if (!paths) return onChange(null)
    onChange(
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + Math.round(w) + ' ' + Math.round(h) +
        '" width="' + Math.round(w) + '" height="' + Math.round(h) + '">' + paths + '</svg>'
    )
  }

  const at = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    const rect = canvas.getBoundingClientRect()
    return { x: clientX - rect.left, y: clientY - rect.top }
  }

  const begin = (clientX: number, clientY: number) => {
    if (drawing.current) return
    size()
    drawing.current = true
    current.current = [at(clientX, clientY)]
  }

  const extend = (clientX: number, clientY: number) => {
    if (!drawing.current) return
    const p = at(clientX, clientY)
    const prev = current.current[current.current.length - 1]
    if (prev && Math.abs(prev.x - p.x) < 0.4 && Math.abs(prev.y - p.y) < 0.4) return
    current.current.push(p)
    const ctx = canvasRef.current && canvasRef.current.getContext('2d')
    if (ctx && prev) {
      ctx.beginPath()
      ctx.moveTo(prev.x, prev.y)
      ctx.lineTo(p.x, p.y)
      ctx.stroke()
    }
    if (!hasInk) setHasInk(true)
  }

  const finish = () => {
    if (!drawing.current) return
    drawing.current = false
    if (current.current.length > 1) strokes.current.push(current.current)
    current.current = []
    emit()
  }

  // Safari drops pointermove on a canvas part way through a gesture. The previous
  // version arbitrated by whichever event family opened the gesture, so on iOS
  // pointerdown claimed it, pointermove then went missing, and the touch handlers -
  // the ones that do keep firing there - were suppressed for the rest of the stroke.
  // Desktop drew fine, the iPhone drew nothing at all. Both families now feed one
  // gesture: begin, extend and finish are idempotent and repeat points are dropped,
  // so it does not matter which family a browser sends, or whether it sends both.
  const debugOn = typeof window !== 'undefined' && window.location.search.indexOf('debug=sig') >= 0
  const [diag, setDiag] = useState({ pd: 0, pm: 0, ts: 0, tm: 0 })

  const note = (k: 'pd' | 'pm' | 'ts' | 'tm') => {
    if (!debugOn) return
    setDiag((d) => {
      const next = { pd: d.pd, pm: d.pm, ts: d.ts, tm: d.tm }
      next[k] = next[k] + 1
      return next
    })
  }

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    note('pd')
    begin(e.clientX, e.clientY)
  }

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return
    note('pm')
    extend(e.clientX, e.clientY)
  }

  const onPointerUp = () => {
    finish()
  }

  // Wired natively rather than through React so that passive is false. React
  // registers touchstart and touchmove passively at the root, which makes
  // preventDefault a no-op and lets iOS reclaim the stroke as a page pan.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const start = (e: TouchEvent) => {
      const t = e.touches[0]
      if (!t) return
      e.preventDefault()
      note('ts')
      begin(t.clientX, t.clientY)
    }
    const move = (e: TouchEvent) => {
      const t = e.touches[0]
      if (!t || !drawing.current) return
      e.preventDefault()
      note('tm')
      extend(t.clientX, t.clientY)
    }
    const end = (e: TouchEvent) => {
      e.preventDefault()
      finish()
    }
    canvas.addEventListener('touchstart', start, { passive: false })
    canvas.addEventListener('touchmove', move, { passive: false })
    canvas.addEventListener('touchend', end, { passive: false })
    canvas.addEventListener('touchcancel', end, { passive: false })
    return () => {
      canvas.removeEventListener('touchstart', start)
      canvas.removeEventListener('touchmove', move)
      canvas.removeEventListener('touchend', end)
      canvas.removeEventListener('touchcancel', end)
    }
  })

  const clear = () => {
    strokes.current = []
    current.current = []
    drawing.current = false
    setHasInk(false)
    const canvas = canvasRef.current
    const ctx = canvas && canvas.getContext('2d')
    if (ctx && canvas) ctx.clearRect(0, 0, canvas.width, canvas.height)
    onChange(null)
  }

  return (
    <div className="sp-pad-wrap">
      <canvas
        ref={canvasRef}
        className="sp-pad"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onPointerCancel={onPointerUp}
      />
      <div className="sp-pad-foot">
        <span>{hasInk ? 'Sign above' : 'Draw your signature above with your finger or mouse'}</span>
        <button type="button" className="sp-link" onClick={clear} disabled={!hasInk}>
          Clear
        </button>
      </div>
      {debugOn ? (
        <p style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6, marginTop: 10 }}>
          <span>pointerdown {diag.pd} / pointermove {diag.pm}</span>
          <br />
          <span>touchstart {diag.ts} / touchmove {diag.tm}</span>
        </p>
      ) : null}
    </div>
  )
}

/* --------------------------------------------------------------- upload --- */

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result || '').replace(/^data:[^;]+;base64,/, ''))
    r.onerror = () => reject(new Error('That file could not be read.'))
    r.readAsDataURL(file)
  })
}

function DocRow({
  doc,
  token,
  onUploaded,
}: {
  doc: RequiredDoc
  token: string
  onUploaded: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const pick = async (file?: File) => {
    if (!file) return
    setErr(null)
      setNote(null)
    setBusy(true)
    try {
      const dataBase64 = await readAsBase64(file)
      const res = await fetch('/api/seller/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          documentType: doc.key,
          filename: file.name,
          contentType: file.type,
          dataBase64,
        }),
      })
      const body = await res.json().catch(() => null)
      // Not res.ok alone. The body has to say it worked.
      if (!res.ok || !body?.success) {
        setErr(body?.error || 'That upload did not work. Please try again.')
      } else {
          // The endpoint says when its own checks could not run. The page above
          // promises the seller we look at each file as it arrives, so on the one
          // occasion nothing was looked at they hear it, rather than get a tick.
          if (body?.note) setNote(String(body.note))
        onUploaded()
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'That upload did not work. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <li className={`sp-doc ${doc.received ? 'done' : ''}`}>
      <div className="sp-doc-main">
        <span className="sp-doc-tick">{doc.received ? '✓' : ''}</span>
        <div>
          <div className="sp-doc-label">
            {doc.label}
            {doc.optional && <span className="sp-optional">optional</span>}
          </div>
          {/*
            Winnie, 1 Sep: tell a company seller what their invoice has to carry,
            at the point they are about to upload it - not in an email they read
            days ago.
          */}
          {doc.key === 'tax_invoice_or_abn_invoice' && (
            <div className="sp-doc-hint">
              Please ensure the invoice includes your company&rsquo;s ABN, GST details (if
              applicable), the vehicle VIN, and the agreed sale amount.
            </div>
          )}
          {err && <div className="sp-doc-err">{err}</div>}
          {note && <div className="sp-doc-note">{note}</div>}
        </div>
      </div>
      <div className="sp-doc-action">
        <FileDropZone
          accept="image/*,application/pdf"
          label={doc.received ? 'Replace' : 'Upload'}
          busy={busy}
          busyLabel="Uploading…"
          onFiles={(files) => pick(files[0])}
        />
      </div>
    </li>
  )
}

/* ---------------------------------------------------------------- page --- */

export default function SellerPortal() {
  const { token = '' } = useParams()
  const [data, setData] = useState<PortalData | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const [signature, setSignature] = useState<string | null>(null)
  const [typedName, setTypedName] = useState('')
  const [agreed, setAgreed] = useState(false)
  // Winnie, 1 Sep: checkpoint (b). Three-state on purpose - null means
  // "not answered yet", which must never be allowed to read as No.
  const [payoutApplicable, setPayoutApplicable] = useState<boolean | null>(null)
  // One box per declaration. A single tick-all would record thirteen affirmations
  // the seller never read one by one, and this document gets read in disputes.
  const [affirmed, setAffirmed] = useState<number[]>([])
  const [termsOpen, setTermsOpen] = useState(false)
  const [signing, setSigning] = useState(false)
  const [signError, setSignError] = useState<string | null>(null)

  const [legalName, setLegalName] = useState('')
  const [nameSaving, setNameSaving] = useState(false)
  const [nameSaved, setNameSaved] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)

  // Step 1 confirms three things now. The address prints on the contract and
  // the mobile carries the signing link and the inspection SMS, so both are
  // worth a look from the one person who knows whether they are right.
  const [address, setAddress] = useState('')
  const [mobile, setMobile] = useState('')
  const [email, setEmail] = useState('')

  /*
   * Winnie, 1 Sep: "Seller information should draw out from the vehicle
   * purchase agreement when they confirm their details."
   *
   * These five printed "Not recorded" on every contract, because Step 1 never
   * asked for them. All of them are optional: a private seller has no ABN and
   * no LMCT, and a missing suburb must never be the reason a contract does
   * not get signed.
   */
  const [suburb, setSuburb] = useState('')
  const [stateAbbrev, setStateAbbrev] = useState('')
  const [postcode, setPostcode] = useState('')
  const [abn, setAbn] = useState('')
  const [lmct, setLmct] = useState('')

  // Separate from `agreed`, which is about owning the car and selling it at
  // the agreed price. This one is about the eighteen clauses above it.
  const [termsAccepted, setTermsAccepted] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`/api/seller/${encodeURIComponent(token)}`)
      const body = await res.json().catch(() => null)
      if (!res.ok || !body?.success) {
        // A response we could not even parse is not the same as a link that
        // has been revoked. Telling a seller their link is dead when the real
        // problem is at our end sends them to the phone for nothing — and it
        // is exactly what happened when Cloudflare replaced our 502 with its
        // own HTML error page.
        setLoadError(
          body?.error ||
            (body === null
              ? "We could not load this right now. Your link is fine — please try again in a minute."
              : 'This link is not valid any more.')
        )
        setData(null)
      } else {
        setData(body as PortalData)
        setLoadError(null)
        if (!typedName && body.seller?.name) setTypedName(body.seller.name)
      }
    } catch {
      setLoadError('We could not load this right now. Please check your connection and try again.')
    } finally {
      setLoading(false)
    }
    // typedName is deliberately not a dependency: refreshing after an upload
    // must not overwrite what the seller has typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  useEffect(() => {
    load()
  }, [load])

  /* The name that goes on the contract is the one confirmed in step 1, not something
     retyped at the moment of signing - retyping it is one more chance to sign in the
     wrong name, and by then it is too late to be a correction.

     This resets whenever the server data reloads, which is what we want: the server is
     the record, and an edit that was never saved should not survive a refresh looking
     as though it had been. */
  useEffect(() => {
    if (!data) return
    const onRecord = data.seller.legalName || data.seller.name || ''
    setLegalName(onRecord)
    setTypedName(onRecord)
    setAddress(data.seller.address || '')
    setMobile(data.seller.mobile || '')
    setEmail(data.seller.email || '')
    setSuburb(data.seller.suburb || '')
    setStateAbbrev(data.seller.state || '')
    setPostcode(data.seller.postcode || '')
    setAbn(data.seller.abn || '')
    setLmct(data.seller.lmct || '')
  }, [data])

  const saveName = async () => {
    setNameError(null)
    setNameSaved(false)
    const wanted = legalName.trim().replace(/\s+/g, ' ')
    if (wanted.length < 2) {
      setNameError('Please enter the full name the vehicle is registered in.')
      return
    }
    if (address.trim().length < 5) {
      setNameError('Please enter the address the vehicle is registered to')
      return
    }

    if ((mobile.match(/\d/g) || []).length < 8) {
      setNameError('Please enter a mobile number we can reach you on')
      return
    }
    if (!/^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(email.trim())) {
      setNameError('Please enter an email address we can send the contract to')
      return
    }


    setNameSaving(true)
    try {
      const res = await fetch('/api/seller/name', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          legalName: wanted,
          address: address.trim(),
          mobile: mobile.trim(),
          email: email.trim(),
          suburb: suburb.trim(),
          state: stateAbbrev.trim(),
          postcode: postcode.trim(),
          abn: abn.trim(),
          lmct: lmct.trim(),
        }),
      })
      const body = await res.json().catch(() => null)
      if (!res.ok || !body?.success) {
        setNameError(body?.error || 'We could not save that just now. Please try again.')
      } else {
        setNameSaved(true)
        await load()
      }
    } catch {
      setNameError('We could not save that just now. Please try again.')
    } finally {
      setNameSaving(false)
    }
  }

  const sign = async () => {
    setSignError(null)
    if (!signature) return setSignError('Please draw your signature in the box.')
    if (typedName.trim().length < 2) return setSignError('Please type your full name.')
    const decls = data && data.billOfSale.declarations ? data.billOfSale.declarations : []
    const missing = decls.length - affirmed.length
    if (missing > 0)
      return setSignError(
        missing === 1
          ? 'One declaration is still unticked. If you cannot confirm it, reply to the message we sent you instead of signing.'
          : String(missing) + ' declarations are still unticked. If you cannot confirm them all, reply to the message we sent you instead of signing.'
      )
    if (!termsAccepted)
      return setSignError('Please tick the box to confirm you accept the terms and conditions')
    if (!agreed) return setSignError('Please tick the box to confirm you agree to the sale.')
    if (payoutApplicable !== true && payoutApplicable !== false)
      return setSignError('Please answer whether there is finance owing on this vehicle.')

    setSigning(true)
    try {
      const res = await fetch('/api/seller/sign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, signatureSvg: signature, typedName: typedName.trim(), agreed: true,
            financePayoutApplicable: payoutApplicable,
            termsAccepted: true, declarationsAffirmed: affirmed.slice().sort((a, b) => a - b) }),
      })
      const body = await res.json().catch(() => null)
      if (!res.ok || !body?.success) {
        setSignError(body?.error || 'We could not record your signature. Nothing has been signed — please try again.')
      } else {
        await load()
      }
    } catch {
      setSignError('We could not record your signature. Nothing has been signed — please try again.')
    } finally {
      setSigning(false)
    }
  }

  if (loading && !data) {
    return (
      <div className="sp-shell">
        <div className="sp-card sp-quiet">Loading…</div>
      </div>
    )
  }

  if (loadError || !data) {
    return (
      <div className="sp-shell">
        <header className="sp-head">
          <div className="sp-logo">DA</div>
          <div>
            <div className="sp-brand">Direct Auto</div>
          </div>
        </header>
        <div className="sp-card">
          <h1>We can't open this link</h1>
          <p>{loadError}</p>
          <p className="sp-quiet">
            If you were expecting to sign a Vehicle Purchase Agreement or send us documents, reply to the message we sent
            you and we'll issue a fresh link.
          </p>
        </div>
      </div>
    )
  }

  const bos = data.billOfSale
  const docs = data.documents

  // An ABN is asked for from a business seller and an LMCT only from a
  // licensed dealer. Putting either in front of a private seller invites a
  // wrong answer in a box that ends up on a contract.
  const sellerType = String(data.seller.type || 'individual').toLowerCase()
  const isBusinessSeller = sellerType !== 'individual'
  const isLicensedDealer = sellerType === 'dealer'
  const outstanding = docs.required.filter((d) => !d.received && !d.optional).length

  return (
    <div className="sp-shell">
      <header className="sp-head">
        <div className="sp-logo">DA</div>
        <div>
          <div className="sp-brand">Direct Auto</div>
          <div className="sp-quiet">Hi {data.seller.name || 'there'}</div>
        </div>
      </header>

      {/* -------- the car and the price, so they know they're in the right place -------- */}
      <section className="sp-card">
        <h2 className="sp-eyebrow">Your vehicle</h2>
        <div className="sp-vehicle">{data.vehicle.description || 'Vehicle'}</div>
        <dl className="sp-facts">
          {data.vehicle.rego && (
            <div>
              <dt>Rego</dt>
              <dd>{data.vehicle.rego}</dd>
            </div>
          )}
          {data.vehicle.vin && (
            <div>
              <dt>VIN</dt>
              <dd className="sp-mono">{data.vehicle.vin}</dd>
            </div>
          )}
          {data.vehicle.odometer != null && (
            <div>
              <dt>Odometer</dt>
              <dd>{Number(data.vehicle.odometer).toLocaleString('en-AU')} km</dd>
            </div>
          )}
          <div>
            <dt>Agreed price</dt>
            <dd className="sp-price">{money(data.agreedPrice)}</dd>
          </div>
        </dl>
      </section>

        {/* Step 1 - the name the contract will be made out in.

            This comes first because it is the one thing on a Vehicle Purchase Agreement that has to
            match every document behind it, and because the person who knows whether it is
            right is the seller, not whoever typed it into the deal. */}
        <section className="sp-card">
          <h2 className="sp-eyebrow">Step 1 &middot; Your details</h2>

          <label className="sp-label" htmlFor="sp-legal">
            The full name the vehicle is registered in
          </label>
          <input
            id="sp-legal"
            className="sp-input"
            value={legalName}
            onChange={(e) => {
              setLegalName(e.target.value)
              setNameSaved(false)
            }}
            autoComplete="name"
            disabled={bos.status === 'signed'}
          />
          <p className="sp-quiet">
            This is the name that will go on the Vehicle Purchase Agreement, so it needs to match your
            licence and the registration certificate. Fix it here if it is wrong - once you
            have signed, it cannot be changed.
          </p>

          <label className="sp-label" htmlFor="sp-address">
            Your address
          </label>
          <input
            id="sp-address"
            className="sp-input"
            value={address}
            onChange={(e) => {
              setAddress(e.target.value)
              setNameSaved(false)
            }}
            autoComplete="street-address"
            disabled={bos.status === 'signed'}
          />
                    <label className="sp-label" htmlFor="sp-suburb">
            Suburb
          </label>
          <input
            id="sp-suburb"
            className="sp-input"
            value={suburb}
            onChange={(e) => {
              setSuburb(e.target.value)
              setNameSaved(false)
            }}
            autoComplete="address-level2"
            disabled={bos.status === 'signed'}
          />
          <label className="sp-label" htmlFor="sp-state">
            State
          </label>
          <input
            id="sp-state"
            className="sp-input"
            value={stateAbbrev}
            onChange={(e) => {
              setStateAbbrev(e.target.value)
              setNameSaved(false)
            }}
            autoComplete="address-level1"
            disabled={bos.status === 'signed'}
          />
          <label className="sp-label" htmlFor="sp-postcode">
            Postcode
          </label>
          <input
            id="sp-postcode"
            className="sp-input"
            value={postcode}
            onChange={(e) => {
              setPostcode(e.target.value)
              setNameSaved(false)
            }}
            autoComplete="postal-code"
            inputMode="numeric"
            disabled={bos.status === 'signed'}
          />


          <label className="sp-label" htmlFor="sp-mobile">
            Your mobile number
          </label>
          <input
            id="sp-mobile"
            className="sp-input"
            type="tel"
            inputMode="tel"
            value={mobile}
            onChange={(e) => {
              setMobile(e.target.value)
              setNameSaved(false)
            }}
            autoComplete="tel"
            disabled={bos.status === 'signed'}
          />

          <label className="sp-label" htmlFor="sp-email">
            Your email address
          </label>
          <input
            id="sp-email"
            className="sp-input"
            type="email"
            inputMode="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              setNameSaved(false)
            }}
            autoComplete="email"
            disabled={bos.status === 'signed'}
          />

          {isBusinessSeller && (
            <>
            <label className="sp-label" htmlFor="sp-abn">
              ABN / ACN
            </label>
            <input
              id="sp-abn"
              className="sp-input"
              value={abn}
              onChange={(e) => {
                setAbn(e.target.value)
                setNameSaved(false)
              }}
              inputMode="numeric"
              disabled={bos.status === 'signed'}
            />
            </>
          )}

          {isLicensedDealer && (
            <>
            <label className="sp-label" htmlFor="sp-lmct">
              LMCT number
            </label>
            <input
              id="sp-lmct"
              className="sp-input"
              value={lmct}
              onChange={(e) => {
                setLmct(e.target.value)
                setNameSaved(false)
              }}
              disabled={bos.status === 'signed'}
            />
            </>
          )}

          <p className="sp-quiet">
            The address prints on the Vehicle Purchase Agreement. The mobile is where the
            signing link and the inspection time are sent, so it needs to be the
            phone you actually carry.
          </p>

          {nameError ? <div className="sp-error">{nameError}</div> : null}

          {bos.status === 'signed' ? (
            <p className="sp-quiet">Signed, so this is now fixed.</p>
          ) : (
            <>
              <button className="sp-btn" onClick={saveName} disabled={nameSaving}>
                {nameSaving ? 'Saving\u2026' : 'Confirm these details'}
              </button>
              {nameSaved ? <p className="sp-quiet">Saved.</p> : null}
            </>
          )}
        </section>

        {/* Step 2 - the documents. They come before the signature now: a contract signed
            before anyone has checked who is selling the car is a contract signed on trust,
            and the moment it is signed the reason to send anything at all is gone. */}
        <section className="sp-card">
          <h2 className="sp-eyebrow">Step 2 &middot; Your documents</h2>

          {docs.status === 'not_requested' ? (
            <p className="sp-quiet">Nothing needed from you yet. We will be in touch if that changes.</p>
          ) : null}

          {docs.status === 'closed' ? (
            <>
              <p>The upload window for this deal has closed.</p>
              <p className="sp-quiet">Reply to our message and we will reopen it.</p>
            </>
          ) : null}

          {docs.status === 'open' ? (
            <>
              <p>
                {outstanding === 0
                  ? 'That is everything we need, thank you.'
                  : outstanding + (outstanding === 1 ? ' document' : ' documents') + ' still to come. A clear photo from your phone is fine.'}
              </p>
              <p className="sp-quiet">
                We check each one is readable as it arrives and tell you straight away if it
                is not, so nothing sits waiting on a photo nobody could read.
              </p>
              <ul className="sp-docs">
                {docs.required.map((d) => (
                  <DocRow key={d.key} doc={d} token={token} onUploaded={load} />
                ))}
              </ul>
            </>
          ) : null}
        </section>

        {/* Step 3 - the signature, last. */}
        <section className="sp-card">
          <h2 className="sp-eyebrow">Step 3 &middot; Vehicle Purchase Agreement</h2>
          {/* The contract, one click away, whatever stage this is at. It used to
              sit inside the signing branch, which meant it was invisible until
              the documents were in - the seller could not read what they were
              collecting documents for. Same renderer as the filed copy. */}
          {bos.status !== 'not_required' ? (
            <>
              <p className="sp-quiet">
                Read it whenever you like, before your documents are in or after.
                It opens in a new tab, and it is the exact document your signature
                goes on.
              </p>
              <a
                className="sp-btn sp-btn-quiet"
                href={'/api/seller/agreement?token=' + encodeURIComponent(token || '')}
                target="_blank"
                rel="noopener noreferrer"
              >
                View the Vehicle Purchase Agreement
              </a>
            </>
          ) : null}


          {bos.status === 'not_required' ? (
            <>
              <p>There is nothing for you to sign.</p>
              <p className="sp-quiet">
                You are selling to us as a licensed dealer, so your tax invoice is the document that
                carries this sale - not a Vehicle Purchase Agreement. Send that through with your documents above
                and there is nothing further to do here.
              </p>
            </>
          ) : null}

          {bos.status === 'signed' ? (
            <div className="sp-done">
              <div className="sp-done-tick">&#10003;</div>
              <div>
                <b>Signed</b>
                <div className="sp-quiet">
                  {bos.signedByName} &middot; {when(bos.signedAt)}
                </div>
              </div>
            </div>
          ) : null}

          {bos.status === 'waiting_for_documents' ? (
            <>
              <p>We will open this for signing as soon as your documents are in.</p>
              <p className="sp-quiet">
                {outstanding === 1
                  ? 'One document is still to come.'
                  : outstanding + ' documents are still to come.'}
                {' '}
                It is this way round on purpose: the contract is made out in a name we have
                checked against your own paperwork, which protects you as much as us.
              </p>
            </>
          ) : null}

          {bos.status === 'closed' ? (
            <>
              <p>This signing link has expired.</p>
              <p className="sp-quiet">
                Reply to the message we sent you and we will issue a new one. Nothing is lost.
              </p>
            </>
          ) : null}

          {bos.status === 'awaiting_signature' ? (
            <>
              {/* Documents come before the signature. Winnie is right that a
                  signature taken before the documents are in is a signature taken
                  before anyone has checked the person signing owns the car - but a
                  hard block strands a seller who cannot photograph a licence at 9pm,
                  so this warns instead of refusing. */}
              {outstanding > 0 && (
                <p className="sp-warn">
                  We still need {outstanding}{' '}
                  {outstanding === 1 ? 'document' : 'documents'} from you in step 2
                  above. You can sign now, but we cannot pay you until they are in.
                </p>
              )}

              <p>
                By signing you agree to sell the vehicle above to Direct Auto for{' '}
                <b>{money(data.agreedPrice)}</b>.
              </p>

              <div className="sp-label">Signing as</div>
              <p>
                <b>{data.seller.legalName || data.seller.name}</b>
              </p>
              <p className="sp-quiet">
                This is the name you confirmed in step 1. If it is wrong, correct it up there
                before you sign, not afterwards.
              </p>

              <label className="sp-label">Your signature</label>
              <SignaturePad onChange={setSignature} />

              <div className="sp-label">Your declarations</div>
              <p className="sp-quiet">
                These are printed on the Vehicle Purchase Agreement. Tick each one you can confirm. If any
                is not true for your vehicle, do not sign - reply to the message we sent you
                and we will sort it out.
              </p>
              {/*
                * Winnie, 1 Sep: "Default to all with ticks or an option to
                * 'tick all' when they select I have read..."
                *
                * This is the second half of what she asked for, not the first.
                * Ticking them by default would mean the system made the
                * warranty, not the seller - and these are the clauses Direct
                * Auto would most want to stand on if a car turned out to be
                * stolen or written off. A button the seller presses is still
                * the seller's act, and every box stays visible and can be
                * unticked one at a time.
                */}
              {(data.billOfSale.declarations || []).length > 0 && (
                <button
                  type="button"
                  className="sp-btn-quiet"
                  onClick={() =>
                    setAffirmed((prev) =>
                      prev.length === (data.billOfSale.declarations || []).length
                        ? []
                        : (data.billOfSale.declarations || []).map((_, i) => i),
                    )
                  }
                >
                  {affirmed.length === (data.billOfSale.declarations || []).length
                    ? 'Clear all'
                    : 'Tick all'}
                </button>
              )}
              {(data.billOfSale.declarations || []).map((d, i) => (
                <label className="sp-check" key={i}>
                  <input
                    type="checkbox"
                    checked={affirmed.indexOf(i) >= 0}
                    onChange={(e) =>
                      setAffirmed((prev) =>
                        e.target.checked ? prev.concat([i]) : prev.filter((x) => x !== i),
                      )
                    }
                  />
                  <span>{d}</span>
                </label>
              ))}
              <p className="sp-quiet">
                {affirmed.length} of {(data.billOfSale.declarations || []).length} confirmed
              </p>

              {(data.billOfSale.clauses || []).length > 0 ? (
                <div className="sp-terms">
                  <button type="button" className="sp-link" onClick={() => setTermsOpen(!termsOpen)}>
                    {termsOpen ? (
                      <>Hide the full terms</>
                    ) : (
                      <>Read the full terms ({(data.billOfSale.clauses || []).length} clauses)</>
                    )}
                  </button>
                  {termsOpen ? (
                    <ol className="sp-clauses">
                      {(data.billOfSale.clauses || []).map((c, i) => (
                        <li key={i}>
                          <b>{c.title}</b> {c.body}
                        </li>
                      ))}
                    </ol>
                  ) : null}
                </div>
              ) : null}


              <label className="sp-check">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <span>
                  I have read the terms and conditions above, and I accept them.
                </span>
              </label>

              {/*
                * Winnie, 1 Sep: checkpoint (b). The seller is the party who actually
                * knows whether finance is owing, so they answer it themselves rather
                * than the deal running on Sales' answer alone. Two radios, no default -
                * a tick-box cannot tell "No" apart from "not answered", and that
                * difference is the entire point of the check.
                */}
              <div className="sp-label">Is there finance owing on this vehicle?</div>
              <p className="sp-quiet">
                If any money is still owed to a bank or finance company on this car, answer Yes.
                We will need a payout letter from them before we can pay you. This question must
                be answered before your signature can be accepted.
              </p>
              <label className="sp-check">
                <input
                  type="radio"
                  name="finance-payout"
                  checked={payoutApplicable === true}
                  onChange={() => setPayoutApplicable(true)}
                />
                <span>Yes - there is finance owing on this vehicle and it needs to be paid out.</span>
              </label>
              <label className="sp-check">
                <input
                  type="radio"
                  name="finance-payout"
                  checked={payoutApplicable === false}
                  onChange={() => setPayoutApplicable(false)}
                />
                <span>No - the vehicle is owned outright, with nothing owing on it.</span>
              </label>

              <label className="sp-check">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                <span>
                  I am the owner, or authorised to act for the owner, of this vehicle, and I agree to sell it to
                  Direct Auto for {money(data.agreedPrice)}.
                </span>
              </label>

              {signError ? <div className="sp-error">{signError}</div> : null}

              <button className="sp-btn" onClick={sign} disabled={signing}>
                {signing ? 'Signing\u2026' : 'Sign the Vehicle Purchase Agreement'}
              </button>
            </>
          ) : null}
        </section>

      <footer className="sp-foot">
        <p>
          Direct Auto — please review carefully. If anything looks incorrect, contact your Sales representative.
        </p>
      </footer>
    </div>
  )
}
